import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api/use-api';
import {
  PAGE_SIZE,
  type Batch,
  type GetReceiptResponse,
  type GetTransferResponse,
  type GetWriteOffResponse,
  type ListOpenBatchesResponse,
  type ListParams,
  type ListReceiptsResponse,
  type ListTransfersResponse,
  type ListWriteOffsResponse,
  type Receipt,
  type ReceiptRequest,
  type ReceiptSummary,
  type Transfer,
  type TransferRequest,
  type TransferSummary,
  type WriteOff,
  type WriteOffKind,
  type WriteOffRequest,
  type WriteOffSummary,
} from './types';

/**
 * API склада. Данные организационные — токен по умолчанию.
 *
 * Один ключ на весь склад: любое изменение накладной или списания меняет
 * остатки, поэтому после мутаций сбрасывается всё разом.
 */
const KEY = 'warehouse';

function listQuery(path: string, params: ListParams, cursor: string): string {
  const query = new URLSearchParams({ objectId: params.objectId, pageSize: String(PAGE_SIZE) });
  if (params.search) query.set('search', params.search);
  if (cursor) query.set('cursor', cursor);

  return `${path}?${query.toString()}`;
}

/** Страницы накладных объекта: свежие сверху, следующая — по курсору. */
export function useReceipts(params: ListParams) {
  const api = useApi();

  return useInfiniteQuery({
    queryKey: [KEY, api.organizationId, 'receipts', params.objectId, params.search],
    queryFn: ({ pageParam }) =>
      api.request<ListReceiptsResponse>(listQuery('/v1/warehouse/receipts', params, pageParam)),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: api.organizationId !== null && params.objectId !== '',
  });
}

/** Накладная целиком, со строками. */
export function useReceipt(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: [KEY, api.organizationId, 'receipt', id],
    queryFn: () => api.request<GetReceiptResponse>(`/v1/warehouse/receipts/${id}`),
    enabled: api.organizationId !== null && id !== null,
  });
}

/** Запрос, на котором держатся пакетные функции склада. */
export type WarehouseApi = ReturnType<typeof useApi>;

/**
 * Все накладные объекта одним запросом: журнал печатает период целиком, а не
 * страницу из пятидесяти. Потолок бэкенда на pageSize — 10000, дальше только
 * курсор; таких объёмов за период журнала не бывает.
 */
export async function fetchAllReceipts(
  api: WarehouseApi,
  objectId: string
): Promise<ReceiptSummary[]> {
  const query = new URLSearchParams({ objectId, pageSize: String(10000) });
  const page = await api.request<ListReceiptsResponse>(
    `/v1/warehouse/receipts?${query.toString()}`
  );

  return page.receipts;
}

/** Накладные целиком по списку id: у списка нет ни строк, ни партий. */
export async function fetchReceiptsByIds(api: WarehouseApi, ids: string[]): Promise<Receipt[]> {
  const pages = await Promise.all(
    ids.map((id) => api.request<GetReceiptResponse>(`/v1/warehouse/receipts/${id}`))
  );

  return pages.map((page) => page.receipt);
}

/** Все списания объекта, ручные и автоматические: страницы по курсору подряд. */
export async function fetchAllWriteOffs(
  api: WarehouseApi,
  objectId: string
): Promise<WriteOffSummary[]> {
  const out: WriteOffSummary[] = [];
  for (const kind of ['manual', 'auto'] satisfies WriteOffKind[]) {
    let cursor = '';
    do {
      const query = new URLSearchParams({ objectId, kind, pageSize: String(10000) });
      if (cursor) query.set('cursor', cursor);
      const page = await api.request<ListWriteOffsResponse>(
        `/v1/warehouse/write-offs?${query.toString()}`
      );
      out.push(...page.writeOffs);
      cursor = page.nextCursor;
    } while (cursor);
  }

  return out;
}

/** Списания целиком по списку id. */
export async function fetchWriteOffsByIds(api: WarehouseApi, ids: string[]): Promise<WriteOff[]> {
  const pages = await Promise.all(
    ids.map((id) => api.request<GetWriteOffResponse>(`/v1/warehouse/write-offs/${id}`))
  );

  return pages.map((page) => page.writeOff);
}

/** Все перемещения организации: страницы по курсору подряд. */
export async function fetchAllTransfers(api: WarehouseApi): Promise<TransferSummary[]> {
  const out: TransferSummary[] = [];
  let cursor = '';
  do {
    const query = new URLSearchParams({ pageSize: String(10000) });
    if (cursor) query.set('cursor', cursor);
    const page = await api.request<ListTransfersResponse>(
      `/v1/warehouse/transfers?${query.toString()}`
    );
    out.push(...page.transfers);
    cursor = page.nextCursor;
  } while (cursor);

  return out;
}

/** Перемещения целиком по списку id. */
export async function fetchTransfersByIds(api: WarehouseApi, ids: string[]): Promise<Transfer[]> {
  const pages = await Promise.all(
    ids.map((id) => api.request<GetTransferResponse>(`/v1/warehouse/transfers/${id}`))
  );

  return pages.map((page) => page.transfer);
}

/** Партии объекта с ненулевым остатком — то же, что useOpenBatches, но одним вызовом. */
export async function fetchOpenBatches(api: WarehouseApi, objectId: string): Promise<Batch[]> {
  const page = await api.request<ListOpenBatchesResponse>(
    `/v1/warehouse/batches?${new URLSearchParams({ objectId }).toString()}`
  );

  return page.batches;
}

/** Страницы списаний объекта одного типа: свежие сверху, следующая — по курсору. */
export function useWriteOffs(params: ListParams, kind: WriteOffKind) {
  const api = useApi();

  return useInfiniteQuery({
    queryKey: [KEY, api.organizationId, 'write-offs', params.objectId, params.search, kind],
    queryFn: ({ pageParam }) =>
      api.request<ListWriteOffsResponse>(
        `${listQuery('/v1/warehouse/write-offs', params, pageParam)}&kind=${kind}`
      ),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: api.organizationId !== null && params.objectId !== '',
  });
}

/** Списание целиком, со строками. */
export function useWriteOff(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: [KEY, api.organizationId, 'write-off', id],
    queryFn: () => api.request<GetWriteOffResponse>(`/v1/warehouse/write-offs/${id}`),
    enabled: api.organizationId !== null && id !== null,
  });
}

/** Страницы перемещений: всё, что между складами организации. */
export function useTransfers(search: string) {
  const api = useApi();

  return useInfiniteQuery({
    queryKey: [KEY, api.organizationId, 'transfers', search],
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ pageSize: String(PAGE_SIZE) });
      if (search) query.set('search', search);
      if (pageParam) query.set('cursor', pageParam);

      return api.request<ListTransfersResponse>(`/v1/warehouse/transfers?${query.toString()}`);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    enabled: api.organizationId !== null,
  });
}

/** Перемещение целиком, со строками. */
export function useTransfer(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: [KEY, api.organizationId, 'transfer', id],
    queryFn: () => api.request<GetTransferResponse>(`/v1/warehouse/transfers/${id}`),
    enabled: api.organizationId !== null && id !== null,
  });
}

/** Партии объекта с ненулевым остатком. */
export function useOpenBatches(objectId: string) {
  const api = useApi();

  return useQuery({
    queryKey: [KEY, api.organizationId, 'batches', objectId],
    queryFn: () =>
      api.request<ListOpenBatchesResponse>(
        `/v1/warehouse/batches?objectId=${encodeURIComponent(objectId)}`
      ),
    enabled: api.organizationId !== null && objectId !== '',
  });
}

/**
 * Сроки годности открытых партий: id партии → гггг-мм-дд. У партии срока нет,
 * он хранится в строке накладной, а id партии и есть id этой строки — поэтому
 * подтягиваем накладные партий целиком.
 */
export function useBatchExpiry(batches: Batch[] | undefined) {
  const api = useApi();
  const receiptIds = [...new Set((batches ?? []).map((batch) => batch.receiptId))].sort();

  return useQuery({
    queryKey: [KEY, api.organizationId, 'batch-expiry', receiptIds],
    queryFn: async () => {
      const receipts = await fetchReceiptsByIds(api, receiptIds);
      const expiry: Record<string, string> = {};
      for (const receipt of receipts) {
        for (const item of receipt.items) expiry[item.id] = item.expiryDate;
      }
      return expiry;
    },
    enabled: api.organizationId !== null && receiptIds.length > 0,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: [KEY] });
}

export function useCreateReceipt() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: ReceiptRequest) =>
      api.request<{ id: string }>('/v1/warehouse/receipts', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useUpdateReceipt() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReceiptRequest }) =>
      api.request<void>(`/v1/warehouse/receipts/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

export function useCreateWriteOff() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: WriteOffRequest) =>
      api.request<{ id: string }>('/v1/warehouse/write-offs', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useUpdateWriteOff() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WriteOffRequest }) =>
      api.request<void>(`/v1/warehouse/write-offs/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

export function useCreateTransfer() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (body: TransferRequest) =>
      api.request<{ id: string }>('/v1/warehouse/transfers', { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useUpdateTransfer() {
  const api = useApi();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TransferRequest }) =>
      api.request<void>(`/v1/warehouse/transfers/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}
